import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { BsBoxArrowInUpRight } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { setVendorDetails } from 'actions/vendorDetailsAction';
import FSBModal from 'components/common/FSBModal';
import Loader from 'components/common/Loader';
import NoDataFound from 'components/NoDataFound';
import CustomTabs from 'components/CustomTabs';
import TaskBoard from 'pages/Task';
import { NOTIFICATION_TABS_HEADING } from 'pages/Products/constant';
import {
  MARK_NOTIFICATION_READ,
  NOTIFICATION_QUERY,
  NOTIFICATION_SUBSCRIPTION_QUERY,
} from 'pages/Notifications/NotificationGraphQL';
import { axiosGetAPI } from 'services';
import { authContext } from 'contexts/AuthContext';
import { collectPermissionNames, hasAnyPermission } from 'utils/permissions';
import { DASHBOARD_PERMISSION_KEYS } from 'pages/Dashboard/permissions';
import { ORDER_LIST } from 'utils/CommonGraphQL';
import EditOrderWrapper from 'pages/Orders/components/EditOrderWrapper';
import './styles.css';

const DEFAULT_FILTER = {
  filter: [
    {
      key: 'forAdminOnly',
      value: true,
      type: 'BOOLEAN',
      op: 'EQ',
    },
  ],
  pageNo: 1,
  pageSize: 5, // Show only 5 notifications
  sortCol: null,
  sortDirection: null,
};

const getTabValue = (tab) => {
  if (NOTIFICATION_TABS_HEADING.BIDS === tab) return 'bid';
  if (NOTIFICATION_TABS_HEADING.ORDERS === tab) return 'order';
  if (NOTIFICATION_TABS_HEADING.USERS === tab) return ['trader'];
  if (NOTIFICATION_TABS_HEADING.OFFER === tab) return 'offer';
  if (NOTIFICATION_TABS_HEADING.DEMAND === tab)
    return ['demand', 'NEW_DEMAND'];
  if (NOTIFICATION_TABS_HEADING.OTHERS === tab)
    return ['bid', 'order', 'KYC', 'offer', 'demand', 'NEW_DEMAND', 'trader'];
  return '';
};

const getTaskTabValue = (displayTab) => {
  if (displayTab === 'Assigned to me') return 'assigned';
  if (displayTab === 'Assigned by me') return 'created';
  if (displayTab === 'Escalation Task') return 'escalated';
  return 'assigned';
};

const formatNotificationTime = (timestamp) => {
  return moment(timestamp).format('DD MMM, hh:mm A');
};

const isFreightNotification = (notification) => {
  if (!notification) return false;
  const message = (notification.message || '').toLowerCase();
  const typeName = (notification.typeName || '').toLowerCase();
  return (
    message.includes('freight') ||
    typeName.includes('freight') ||
    typeName === 'freight'
  );
};

const extractOrderId = (notification) => {
  if (!notification) return null;
  const message = notification.message || '';

  if (notification.orderId && !Number.isNaN(Number(notification.orderId))) {
    return Number(notification.orderId);
  }
  if (notification.order?.id && !Number.isNaN(Number(notification.order.id))) {
    return Number(notification.order.id);
  }
  if (notification.entityId && !Number.isNaN(Number(notification.entityId))) {
    return Number(notification.entityId);
  }
  if (
    notification.referenceId &&
    !Number.isNaN(Number(notification.referenceId))
  ) {
    return Number(notification.referenceId);
  }

  const odrDateMatch = message.match(/#ODR_[A-Za-z0-9]+_([0-9,]+)/i);
  if (odrDateMatch?.[1]) {
    const cleanedId = odrDateMatch[1].replace(/,/g, '');
    if (!Number.isNaN(Number(cleanedId)) && Number(cleanedId) > 0) {
      return Number(cleanedId);
    }
  }

  const odrMatch = message.match(/#ODR_([0-9,]+)/i);
  if (odrMatch?.[1]) {
    const cleanedId = odrMatch[1].replace(/,/g, '');
    if (!Number.isNaN(Number(cleanedId)) && Number(cleanedId) > 0) {
      return Number(cleanedId);
    }
  }

  const orderMatch = message.match(/\border\b[^\d]*?([0-9,]+)/i);
  if (orderMatch?.[1]) {
    const cleanedId = orderMatch[1].replace(/,/g, '');
    if (!Number.isNaN(Number(cleanedId)) && Number(cleanedId) > 0) {
      return Number(cleanedId);
    }
  }

  const hashMatch = message.match(/#([0-9,]+)/);
  if (hashMatch?.[1]) {
    const cleanedId = hashMatch[1].replace(/,/g, '');
    if (!Number.isNaN(Number(cleanedId)) && Number(cleanedId) > 0) {
      return Number(cleanedId);
    }
  }

  const forMatch = message.match(/\bfor\b[^\d]*?([0-9,]+)/i);
  if (forMatch?.[1]) {
    const cleanedId = forMatch[1].replace(/,/g, '');
    if (!Number.isNaN(Number(cleanedId)) && Number(cleanedId) > 0) {
      return Number(cleanedId);
    }
  }

  const allNumbers = message.match(/\b\d{3,7}\b/g);
  if (allNumbers?.length) {
    const lastNum = Number(allNumbers[allNumbers.length - 1]);
    if (!Number.isNaN(lastNum) && lastNum > 0) return lastNum;
  }

  return null;
};

const truncateText = (text, maxLength = 60) => {
  if (!text) return '';
  return text.length > maxLength
    ? `${text.substring(0, maxLength)}...`
    : text;
};

const parseTimestampToDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numValue = Number(value);
  if (!Number.isNaN(numValue)) {
    const ms = numValue > 1e12 ? numValue : numValue * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatTaskDate = (dateStr) => {
  if (!dateStr) return null;
  const parsed = parseTimestampToDate(dateStr);
  if (!parsed) return null;
  return moment(parsed).format('MMM DD, YYYY');
};

const isDeletedValue = (value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
};

const getApiListData = (response) => {
  const rawData = response?.data;
  if (Array.isArray(rawData)) return rawData;
  if (Array.isArray(rawData?.content)) return rawData.content;
  if (Array.isArray(rawData?.data)) return rawData.data;
  if (Array.isArray(rawData?.items)) return rawData.items;
  return [];
};

const getPriorityBadgeClass = (priority) => {
  const p = (priority || '').toLowerCase();
  if (p === 'high') {
    return 'bg-danger';
  }
  if (p === 'medium') {
    return 'bg-warning text-dark';
  }
  return 'bg-info text-dark';
};

const NotificationTaskSection = ({ permissions }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auth } = React.useContext(authContext);
  const [selectedTaskDrawer, setSelectedTaskDrawer] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [fetchOrder] = useLazyQuery(ORDER_LIST, {
    fetchPolicy: 'network-only',
  });
  const [markNotificationRead] = useMutation(MARK_NOTIFICATION_READ, {
    refetchQueries: ['notifications'],
    onError: (err) => {
      console.error('Error marking notification as read:', err);
      toast.error(err.message || 'Failed to mark as read');
    },
  });

  const permissionNames = React.useMemo(
    () => collectPermissionNames(auth?.data),
    [auth?.data],
  );

  const canViewEscalationTasks = React.useMemo(
    () =>
      Array.isArray(auth?.data?.roles) &&
      auth.data.roles.some(
        (role) => role?.name === 'SUPER_ADMIN' || role?.name === 'ADMIN',
      ),
    [auth?.data?.roles],
  );

  const taskTabs = React.useMemo(() => {
    const tabs = ['Assigned to me', 'Assigned by me'];
    if (canViewEscalationTasks) {
      tabs.push('Escalation Task');
    }
    return tabs;
  }, [canViewEscalationTasks]);

  const canViewNotifications =
    permissions?.notifications !== undefined
      ? Boolean(permissions.notifications)
      : hasAnyPermission(
          permissionNames,
          DASHBOARD_PERMISSION_KEYS.notifications,
        );

  const canViewTasks =
    permissions?.tasks !== undefined
      ? Boolean(permissions.tasks)
      : hasAnyPermission(permissionNames, DASHBOARD_PERMISSION_KEYS.tasks);

  // Notification States
  const [activeNotificationTab, setActiveNotificationTab] = useState(
    NOTIFICATION_TABS_HEADING.ALL,
  );

  const [notificationFilters, setNotificationFilters] = useState(
    JSON.parse(JSON.stringify(DEFAULT_FILTER)),
  );

  // Task Management States
  const [activeTaskTab, setActiveTaskTab] = useState('assigned');
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  // Fetch Notifications
  const {
    data: notificationData,
    loading: notificationLoading,
    error: notificationError,
    subscribeToMore,
  } = useQuery(NOTIFICATION_QUERY, {
    variables: notificationFilters,
    fetchPolicy: 'network-only',
    skip: !canViewNotifications,
  });

  useEffect(() => {
    if (!canViewNotifications || !subscribeToMore) return;
    const unsubscribe = subscribeToMore({
      document: NOTIFICATION_SUBSCRIPTION_QUERY,
      variables: notificationFilters,
      updateQuery: (prev, { subscriptionData }) => {
        if (!prev || isEmpty(prev)) return '';
        if (!subscriptionData.data) return prev;
        return subscriptionData.data;
      },
    });
    return () => unsubscribe();
  }, [subscribeToMore, notificationFilters, canViewNotifications]);

  // Fetch Tasks
  const fetchTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const userId =
        auth?.data?.userId ??
        auth?.data?.id ??
        auth?.data?.user?.id ??
        auth?.data?.staffId ??
        null;

      if (!userId && activeTaskTab !== 'escalated') {
        setTasks([]);
        setTasksLoading(false);
        return;
      }

      let url = `${process.env.REACT_APP_REST_API_ENDPOINT}`;

      if (activeTaskTab === 'escalated') {
        url += 'tasks/escalated?pageNo=0&pageSize=5';
      } else if (activeTaskTab === 'created') {
        url += `tasks?createdBy=${userId}`;
      } else {
        // Default: assigned to me
        url += `tasks?assignedToId=${userId}`;
      }

      const response = await axiosGetAPI(url, auth);

      if (response?.isSuccessful) {
        const apiTasks = getApiListData(response);

        // Filter out deleted tasks and take only first 5
        const validTasks = Array.isArray(apiTasks)
          ? apiTasks
              .filter((task) => !isDeletedValue(task?.isDeleted))
              .slice(0, 5)
          : [];

        setTasks(validTasks);
      } else {
        setTasks([]);
      }
    } catch {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [activeTaskTab, auth]);

  useEffect(() => {
    if (auth?.data && canViewTasks) {
      fetchTasks();
    }
  }, [auth?.data, fetchTasks, canViewTasks]);

  // Handlers
  const handleNotificationTabChange = (tab) => {
    setActiveNotificationTab(tab);
    if (tab === NOTIFICATION_TABS_HEADING.ALL) {
      setNotificationFilters(JSON.parse(JSON.stringify(DEFAULT_FILTER)));
    } else {
      const value = getTabValue(tab);
      let filterConfig;

      if (
        tab === NOTIFICATION_TABS_HEADING.DEMAND ||
        tab === NOTIFICATION_TABS_HEADING.USERS
      ) {
        filterConfig = {
          key: 'typeName',
          value: value.join(','),
          type: 'STRING',
          op: 'IN',
        };
      } else if (tab === NOTIFICATION_TABS_HEADING.OTHERS) {
        filterConfig = {
          key: 'typeName',
          value: value.join(','),
          type: 'STRING',
          op: 'NOT_IN',
        };
      } else {
        filterConfig = {
          key: 'typeName',
          value,
          type: 'STRING',
          op: 'EQ',
        };
      }

      setNotificationFilters({
        ...JSON.parse(JSON.stringify(DEFAULT_FILTER)),
        filter: [...DEFAULT_FILTER.filter, filterConfig],
      });
    }
  };

  const handleTaskTabChange = (tab) => {
    setActiveTaskTab(tab);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.isAdminOpened && notification?.id) {
      markNotificationRead({ variables: { notificationId: notification.id } });
    }

    const traderData = notification?.trader;
    const message = (notification?.message || '').toLowerCase();

    if (
      /^new\s+for\s+request\s+received\b/i.test(
        notification?.message?.trim() || '',
      )
    ) {
      navigate('/delivered-rate?tab=request-rate&requestTab=customer-request');
      return;
    }

    const orderId = extractOrderId(notification);

    if (message.includes('submitted a purchase request')) {
      if (orderId) {
        navigate(`/orders/${orderId}?tab=Approval`, {
          state: { tab: 'Approval' },
        });
      } else {
        navigate('/orders');
      }
      return;
    }

    if (
      message.includes('freight charge accepted') &&
      message.includes('accept or reject')
    ) {
      if (orderId) {
        navigate(`/orders/${orderId}?tab=Approval`, {
          state: { tab: 'Approval' },
        });
      } else {
        navigate('/orders');
      }
      return;
    }

    const openOrder = async () => {
      try {
        const res = await fetchOrder({
          variables: {
            filter: [{ key: 'id', value: orderId, type: 'INT', op: 'EQ' }],
          },
        });
        const orderData = res?.data?.orders?.orders?.[0];
        if (orderData) {
          setSelectedOrder(orderData);
          setShowEditOrderModal(true);
        } else if (traderData?.id) {
          dispatch(
            setVendorDetails({
              status: true,
              selectedVendor: traderData,
            }),
          );
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        if (traderData?.id) {
          dispatch(
            setVendorDetails({
              status: true,
              selectedVendor: traderData,
            }),
          );
        }
      }
    };

    if (isFreightNotification(notification) && orderId) {
      await openOrder();
      return;
    }

    if (orderId) {
      await openOrder();
      return;
    }

    if (traderData?.id) {
      dispatch(
        setVendorDetails({
          status: true,
          selectedVendor: traderData,
        }),
      );
    }
  };

  const handleTaskClick = (task) => {
    if (!task?.id) return;
    setSelectedTaskDrawer({
      taskId: task.id,
      ownershipTab: activeTaskTab,
    });
  };

  const getSelectedTaskTab = () => {
    if (activeTaskTab === 'assigned') return 'Assigned to me';
    if (activeTaskTab === 'created') return 'Assigned by me';
    if (activeTaskTab === 'escalated') return 'Escalation Task';
    return 'Assigned to me';
  };

  const notifications =
    notificationData?.notifications?.notifications || [];

  const renderNotificationList = () => {
    if (notificationLoading) {
      return (
        <div className="text-center py-3">
          <Loader />
        </div>
      );
    }

    if (notificationError) {
      return (
        <div className="text-center py-3 text-danger">
          Error loading notifications
        </div>
      );
    }

    if (notifications.length === 0) {
      return <NoDataFound message="No notifications available" />;
    }

    return (
      <div className="notification-items">
        {notifications.map((notification, index) => (
          <div
            key={notification?.id || index}
            className="notification-item p-2 mb-2 border-bottom"
            role="button"
            tabIndex={0}
            style={{
              cursor: 'pointer',
              backgroundColor: notification?.isAdminOpened
                ? 'transparent'
                : '#f0f8ff',
            }}
            onClick={() => handleNotificationClick(notification)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleNotificationClick(notification);
              }
            }}
          >
            <div className="d-flex justify-content-between">
              <div className="flex-grow-1">
                <p
                  className="mb-1 small"
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: notification?.isAdminOpened
                      ? 'normal'
                      : '500',
                    color: '#495057',
                  }}
                >
                  {truncateText(notification?.message, 80)}
                </p>
                <small
                  className="text-muted"
                  style={{ fontSize: '0.75rem' }}
                >
                  {formatNotificationTime(notification?.createdAt)}
                </small>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTaskList = () => {
    if (tasksLoading) {
      return (
        <div className="text-center py-3">
          <Loader />
        </div>
      );
    }

    if (tasks.length === 0) {
      return <NoDataFound message="No tasks available" />;
    }

    return (
      <div className="task-items">
        {tasks.map((task, index) => {
          const taskStatus = task?.status || 'pending';
          const isCompleted =
            taskStatus.toLowerCase() === 'completed' ||
            taskStatus.toLowerCase() === 'done';
          const priority = task?.priority || 'Medium';
          const assigneeName =
            task?.assignedToName || task?.assignee || task?.createdByName || '';
          const secondaryContent =
            task?.description ||
            (assigneeName ? `👤 ${assigneeName}` : '');

          return (
            <div
              key={task?.id || index}
              className="task-item py-2 px-3 mb-2 border rounded"
              role="button"
              tabIndex={0}
              style={{
                cursor: 'pointer',
                backgroundColor: isCompleted ? '#f0fdf4' : '#fff',
                borderColor: isCompleted ? '#86efac' : '#dee2e6',
              }}
              onClick={() => handleTaskClick(task)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTaskClick(task);
                }
              }}
            >
              {/* Line 1: Title (left) & Badges (right) */}
              <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
                <div
                  className="text-truncate flex-grow-1"
                  style={{ minWidth: 0 }}
                >
                  <span
                    className="fw-semibold text-dark"
                    style={{ fontSize: '0.85rem' }}
                    title={task?.title || ''}
                  >
                    {task?.title}
                  </span>
                </div>

                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                  {/* Priority Badge */}
                  <span
                    className={`badge ${getPriorityBadgeClass(priority)}`}
                    style={{ fontSize: '0.68rem', fontWeight: '500' }}
                  >
                    {priority}
                  </span>

                  {/* Status Badge */}
                  <span
                    className={`badge ${
                      isCompleted ? 'bg-success text-white' : 'bg-secondary text-white'
                    }`}
                    style={{ fontSize: '0.68rem', fontWeight: '500' }}
                  >
                    {isCompleted ? 'Completed' : 'Pending'}
                  </span>

                  {/* Category Badge */}
                  {task?.category && (
                    <span
                      className="badge bg-light text-dark border"
                      style={{
                        fontSize: '0.68rem',
                        fontWeight: '400',
                      }}
                    >
                      {task.category}
                    </span>
                  )}
                </div>
              </div>

              {/* Line 2: Description (left) & Assignee / Due Date (right) */}
              <div
                className="d-flex justify-content-between align-items-center text-muted gap-2"
                style={{ fontSize: '0.78rem' }}
              >
                <div
                  className="text-truncate flex-grow-1"
                  style={{ minWidth: 0 }}
                  title={secondaryContent}
                >
                  {secondaryContent ? <span>{secondaryContent}</span> : null}
                </div>

                <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-auto">
                  {task?.description && assigneeName && (
                    <span
                      className="text-truncate"
                      style={{ maxWidth: '140px' }}
                      title={assigneeName}
                    >
                      👤 {assigneeName}
                    </span>
                  )}
                  {task?.dueDate && (
                    <span className="text-nowrap">
                      Due: {formatTaskDate(task.dueDate)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!canViewNotifications && !canViewTasks) {
    return null;
  }

  const bothVisible = canViewNotifications && canViewTasks;
  const colSize = bothVisible ? 6 : 12;

  return (
    <>
      <Row className="mt-4 mb-4 notification-task-section">
        {/* Notification Section - Left Half */}
        {canViewNotifications && (
          <Col md={colSize} className="mb-4">
            <Card className="card-box border-0 h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="f-16 mb-0 fw-semibold">Notifications</h4>
                  <button
                    type="button"
                    className="view-all-btn border-0 text-nowrap"
                    onClick={() => navigate('/notification')}
                  >
                    View All <BsBoxArrowInUpRight />
                  </button>
                </div>
  
                <CustomTabs
                  tabs={[
                    NOTIFICATION_TABS_HEADING.ALL,
                    NOTIFICATION_TABS_HEADING.ORDERS,
                    NOTIFICATION_TABS_HEADING.BIDS,
                    NOTIFICATION_TABS_HEADING.USERS,
                    NOTIFICATION_TABS_HEADING.OFFER,
                    NOTIFICATION_TABS_HEADING.DEMAND,
                    NOTIFICATION_TABS_HEADING.OTHERS,
                  ]}
                  selectedTab={activeNotificationTab}
                  onTabChange={handleNotificationTabChange}
                  className="mb-3 dashboard-notification-tabs"
                />
  
                <div className="notification-list">
                  {renderNotificationList()}
                </div>
              </CardBody>
            </Card>
          </Col>
        )}
  
        {/* Task Management Section - Right Half */}
        {canViewTasks && (
          <Col md={colSize} className="mb-4">
            <Card className="card-box border-0 h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="f-16 mb-0 fw-semibold">Task Management</h4>
                  <button
                    type="button"
                    className="view-all-btn border-0 text-nowrap"
                    onClick={() =>
                      navigate('/task', {
                        state: { ownershipTab: activeTaskTab },
                      })
                    }
                  >
                    View All <BsBoxArrowInUpRight />
                  </button>
                </div>
  
                <CustomTabs
                  tabs={taskTabs}
                  selectedTab={getSelectedTaskTab()}
                  onTabChange={(tab) => {
                    const tabValue = getTaskTabValue(tab);
                    handleTaskTabChange(tabValue);
                  }}
                  className="mb-3 dashboard-task-tabs"
                />
  
                <div className="task-list">
                  {renderTaskList()}
                </div>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>
      {selectedTaskDrawer && (
        <TaskBoard
          key={`${selectedTaskDrawer.ownershipTab}-${selectedTaskDrawer.taskId}`}
          embedded
          detailOnly
          initialTaskId={selectedTaskDrawer.taskId}
          initialOwnershipTab={selectedTaskDrawer.ownershipTab}
          onInitialTaskClose={() => setSelectedTaskDrawer(null)}
        />
      )}
      {showEditOrderModal && selectedOrder && (
        <FSBModal
          title="Edit Order"
          closeButtonLabel=""
          confirmButtonLabel=""
          message=""
          open={showEditOrderModal}
          onClose={() => {
            setShowEditOrderModal(false);
            setSelectedOrder(null);
          }}
          toggleAlert={() => {
            setShowEditOrderModal(false);
            setSelectedOrder(null);
          }}
          modalClass="right-drawer-v2"
        >
          <EditOrderWrapper
            currentRow={selectedOrder}
            onCloseOrderPopup={() => {
              setShowEditOrderModal(false);
              setSelectedOrder(null);
            }}
            onOrderUpdated={async () => {
              if (selectedOrder?.id) {
                const res = await fetchOrder({
                  variables: {
                    filter: [
                      {
                        key: 'id',
                        value: selectedOrder.id,
                        type: 'INT',
                        op: 'EQ',
                      },
                    ],
                  },
                });
                if (res?.data?.orders?.orders?.[0]) {
                  setSelectedOrder(res.data.orders.orders[0]);
                }
              }
            }}
          />
        </FSBModal>
      )}
    </>
  );
};

export default NotificationTaskSection;

